import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { WalletReadyState } from '@solana/wallet-adapter-base'
import { useWallet, Wallet } from '@solana/wallet-adapter-react'
import { useWalletModal } from '@solana/wallet-adapter-react-ui'
import { WalletConnectWalletAdapter } from '@solana/wallet-adapter-wallets'

import { Flex, Grid } from '@/components/common/Box'
import { Button } from '@/components/common/Button'
import { ProDialog } from '@/components/common/Dialog'
import { OrbitronText, Text } from '@/components/common/Text'
import { STORAGE_KEY } from '@/constants/common'
import { cn } from '@/lib/utils'

import { isAppWallet } from '../adapters'
import { OkxWalletUIAdapter } from '../adapters/OkxWalletUIAdapter'

const SelectWalletModal: React.FC = () => {
  const { wallets, connected, publicKey, wallet, connect, select } = useWallet()
  const { setVisible, visible } = useWalletModal()

  const [expanded, setExpanded] = useState(false)

  const [listedWallets, collapsedWallets, appWallets] = useMemo(() => {
    const appWallets: Wallet[] = []
    const installed: Wallet[] = []
    const notInstalled: Wallet[] = []

    for (const wallet of wallets) {
      if (isAppWallet(wallet)) {
        appWallets.push(wallet)
      } else if (wallet.readyState === WalletReadyState.Installed) {
        installed.push(wallet)
      } else {
        notInstalled.push(wallet)
      }
    }

    return installed.length
      ? [installed, notInstalled, appWallets]
      : [notInstalled, [], appWallets]
  }, [wallets])

  const handleCollapseClick = useCallback(
    () => setExpanded(!expanded),
    [expanded]
  )
  const handleClose = useCallback(() => setVisible(false), [setVisible])

  const handleSelectWallet = useCallback(
    (wallet: Wallet) => {
      select(wallet.adapter.name)
      handleClose()
      setTimeout(() => {
        // remove before connected
        localStorage.removeItem(STORAGE_KEY.WALLET_NAME)
      }, 0)
    },
    [handleClose, select]
  )

  const renderWalletItem = useCallback(
    (wallet: Wallet) => (
      <Button
        border
        outline
        key={wallet.adapter.name}
        className="justify-between xl:w-[480px]"
        onClick={() => handleSelectWallet(wallet)}
      >
        <Flex className="items-center gap-2">
          <img
            src={wallet.adapter.icon}
            alt={wallet.adapter.name}
            className="size-6 xl:size-8"
          />
          <OrbitronText className="text-base xl:text-lg">
            {wallet.adapter.name}
          </OrbitronText>
        </Flex>
        {wallet.readyState === WalletReadyState.Installed && (
          <Text variant="tertiary" className="text-sm">
            Detected
          </Text>
        )}
      </Button>
    ),
    [handleSelectWallet]
  )

  useEffect(() => {
    if (connected && publicKey && wallet) {
      localStorage.setItem(STORAGE_KEY.WALLET_NAME, `"${wallet.adapter.name}"`)
    }
  }, [publicKey, connected, wallet?.adapter.name, wallet])

  useEffect(() => {
    if (wallet && wallet.readyState === WalletReadyState.NotDetected) {
      if (
        wallet.adapter instanceof OkxWalletUIAdapter ||
        wallet.adapter instanceof WalletConnectWalletAdapter
      ) {
        wallet.adapter.connect()
      } else {
        connect().catch(() => {
          select(null)
        })
      }
    }
  }, [connect, select, wallet])

  return (
    <ProDialog
      open={visible}
      onOpenChange={setVisible}
      contentProps={{ className: 'w-[calc(100vw-24px)] max-w-[480px]' }}
      title={<OrbitronText className="text-lg">Connect Wallet</OrbitronText>}
    >
      <Text className="border-b pb-4" variant="tertiary">
        You need to connect a Solana wallet.
      </Text>
      <div className="max-h-[60vh] space-y-4 overflow-scroll">
        <OrbitronText>Recommended Wallet</OrbitronText>
        <Grid className="gap-2">
          {listedWallets.map(renderWalletItem)}
          {collapsedWallets.length ? (
            <>
              <Flex className="justify-end">
                <button
                  className="flex items-center justify-between gap-2 py-4"
                  onClick={handleCollapseClick}
                  tabIndex={0}
                >
                  <span>{expanded ? 'Less ' : 'More '}options</span>
                  <svg
                    width="13"
                    height="7"
                    viewBox="0 0 13 7"
                    fill="#fff"
                    xmlns="http://www.w3.org/2000/svg"
                    className={cn('transition-all', expanded && 'rotate-180')}
                  >
                    <path d="M0.71418 1.626L5.83323 6.26188C5.91574 6.33657 6.0181 6.39652 6.13327 6.43762C6.24844 6.47872 6.37371 6.5 6.50048 6.5C6.62725 6.5 6.75252 6.47872 6.8677 6.43762C6.98287 6.39652 7.08523 6.33657 7.16774 6.26188L12.2868 1.626C12.7753 1.1835 12.3703 0.5 11.6195 0.5H1.37997C0.629216 0.5 0.224175 1.1835 0.71418 1.626Z" />
                  </svg>
                </button>
              </Flex>
              {expanded && collapsedWallets.map(renderWalletItem)}
            </>
          ) : null}
        </Grid>
        {expanded && appWallets.length && (
          <>
            <OrbitronText>App Wallet</OrbitronText>
            <Grid className="gap-2">{appWallets.map(renderWalletItem)}</Grid>
          </>
        )}
      </div>
    </ProDialog>
  )
}

export default SelectWalletModal
